import { useUserStates } from '@/features/user/stores/user.store';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { useSelectedCases, useSelectedMenuCaseMode, useCasesActions } from '../../../stores/cases.store';
import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import {
  ITeamWithUsersInUserToAssignCase,
  IUserInUserToAssignCase,
  useGetUsersToAssignCaseQuery,
} from '../../../api/get-users-to-assign-case.api';
import { memo, SVGProps, useCallback, useMemo, useState } from 'react';
import { useAssignCases } from '../../../api/assign-cases.api';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import { cn } from '@/utils/cn';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { IUser } from '@/features/user/types/user.type';
import { Search } from 'lucide-react';
import Input from '@/components/ui/input';
import Avatar from '@/components/ui/avatar';
import useSelectedCaseQs from '../../../hooks/use-case-qs';

export type TAssignCaseToOtherModalType = 'AssignCaseToOtherModal';
export type IAssignCaseToOtherModalProps = {
  caseId?: string | null; // Optional: If provided, only assign to this conversation
};

const AssignCaseToOtherModal = memo(({ caseId }: IAssignCaseToOtherModalProps) => {
  // HOOKS
  const { handleSelectedCase } = useSelectedCaseQs();
  const [searchTeam, setSearchTeam] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [selectedUser, setSelectedUser] = useState<IUserInUserToAssignCase | undefined>(undefined);
  const [selectedTeam, setSelectedTeam] = useState<ITeamWithUsersInUserToAssignCase | undefined>(undefined);
  const [selectMode, setSelectMode] = useState<'user' | 'team'>('user');
  const showToast = useCustomToast();
  const { user: userLogged } = useUserStates();
  const selectedCases = useSelectedCases();
  const selectedCaseMode = useSelectedMenuCaseMode();
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
  const { setResetSelectedCases, setResetSelectedMenuCaseMode, setResetSelectedCase } = useCasesActions();

  const isOpenAssignCaseToOtherModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('AssignCaseToOtherModal');
  }, [caseModuleModalRootActions]);

  const { data: usersToAssignCase, isLoading: _isLoadingUsersToAssignCase } = useGetUsersToAssignCaseQuery({
    queryConfig: {
      enabled: !!isOpenAssignCaseToOtherModal,
    },
  });

  // EFFECTS
  // --

  // HELPERS
  const { users, teams } = useMemo(() => {
    return {
      users: usersToAssignCase?.payload?.users || [],
      teams: usersToAssignCase?.payload?.teams || [],
    };
  }, [usersToAssignCase]);

  const filteredUsers = useMemo(() => {
    const result = [];
    const others = [];
    for (const userP of users) {
      if (userP.userId === userLogged?.userId) {
        result.push(userP);
      } else {
        others.push(userP);
      }
    }
    const reorderedUsers = result.concat(others);

    return reorderedUsers.filter((user) => user.fullname.toLowerCase().includes(searchUser.toLowerCase()));
  }, [users, searchUser, userLogged]);

  const filteredTeams = useMemo(() => {
    return teams.filter((team) => team.name.toLowerCase().includes(searchTeam.toLowerCase()));
  }, [teams, searchTeam]);

  const mutationAssignCasesToOther = useAssignCases({
    mutationConfig: {
      onSuccess: () => {
        showToast({
          type: 'success',
          message: 'Assign cases to other successfully',
        });
        caseModuleModalRootActions.closeModal('AssignCaseToOtherModal');
        handleSelectedCase(null);
        setSelectedUser(undefined);
        setSelectedTeam(undefined);
        setSelectMode('user');
        setResetSelectedCases();
        setResetSelectedCase();
        if (selectedCaseMode !== 'idle') {
          setResetSelectedMenuCaseMode();
        }
      },
    },
  });

  // EVENT HANDLERS
  const handleSubmit = useCallback(() => {
    const caseIdsFromStore = selectedCases?.map((item) => item?.caseId) || [];
    const caseIds = caseId ? [caseId] : caseIdsFromStore;

    if (caseIds?.length === 0) {
      showToast({
        type: 'error',
        message: 'Please select at least one case',
      });
      return;
    }
    if (!selectedUser || selectedUser === null || selectedUser?.userId === '') {
      showToast({
        type: 'error',
        message: 'Please select user',
      });
      return;
    }

    if (!selectedTeam) {
      showToast({
        type: 'error',
        message: 'Please select team or to no team',
      });
      return;
    }

    mutationAssignCasesToOther.mutate({
      caseIds,
      userId: selectedUser?.userId || '',
      teamId: selectedTeam?.teamId || null,
    });
  }, [caseId, selectedCases, selectedUser, selectedTeam, mutationAssignCasesToOther, showToast]);

  // EVENT HANDLERS
  const handleCloseModal = useCallback(() => {
    caseModuleModalRootActions.closeModal('AssignCaseToOtherModal');
    if (selectedCaseMode === 'idle') {
      setResetSelectedCases();
    }
  }, [caseModuleModalRootActions, selectedCaseMode, setResetSelectedCases]);

  const disabledButton = useMemo(() => {
    return selectedCases?.length === 0 || !selectedUser || !selectedTeam || mutationAssignCasesToOther.isPending;
  }, [selectedCases, selectedUser, selectedTeam, mutationAssignCasesToOther.isPending]);

  // EARLY RETURN
  if (!isOpenAssignCaseToOtherModal) {
    return null;
  }

  // RENDER LOGIC
  return (
    <Modal
      isCloseOutside={false}
      isButtonClose={true}
      onClose={handleCloseModal}
      className="w-[500px] p-[24px]"
      isOpen={isOpenAssignCaseToOtherModal}
    >
      <ModalHeader>
        <ModalTitle>Assign To Other</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="grid gap-[16px]">
          <div className="flex flex-row items-center gap-[8px]">
            <button
              className={cn('cursor-pointer p-[6px] rounded-[6px] bg-[#F5F5F5] ', {
                'bg-[#E4F8FF]': selectMode === 'user',
              })}
              onClick={(e) => {
                handleDomBubbleEvent(e);
                setSelectedTeam(undefined);
                setSelectedUser(undefined);
                setSelectMode('user');
              }}
            >
              <UserIcon
                className={cn('text-[#9D9D9C]', {
                  'text-[#0C8DBF]': selectMode === 'user',
                })}
              />
            </button>
            <button
              className={cn('cursor-pointer p-[6px] rounded-[6px] bg-[#F5F5F5] ', {
                'bg-[#E4F8FF]': selectMode === 'team',
              })}
              onClick={(e) => {
                handleDomBubbleEvent(e);
                setSelectedTeam(undefined);
                setSelectedUser(undefined);
                setSelectMode('team');
              }}
            >
              <TeamIcon
                className={cn('text-[#9D9D9C]', {
                  'text-[#0C8DBF]': selectMode === 'team',
                })}
              />
            </button>
          </div>

          {selectMode === 'user' ? (
            <UserToTeam
              userLogged={userLogged}
              users={filteredUsers}
              searchUser={searchUser}
              selectedUser={selectedUser}
              selectedTeam={selectedTeam}
              setSearchUser={setSearchUser}
              setSelectedUser={setSelectedUser}
              setSelectedTeam={setSelectedTeam}
            />
          ) : (
            <TeamToUser
              userLogged={userLogged}
              teams={filteredTeams}
              searchTeam={searchTeam}
              selectedTeam={selectedTeam}
              selectedUser={selectedUser}
              setSearchTeam={setSearchTeam}
              setSelectedTeam={setSelectedTeam}
              setSelectedUser={setSelectedUser}
            />
          )}
        </div>
      </ModalContent>
      <ModalFooter className="gap-[12px]">
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleCloseModal();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton onClick={handleSubmit} disabled={disabledButton}>
          {mutationAssignCasesToOther.isPending ? <LoadingSpinner /> : null} Assign
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
AssignCaseToOtherModal.displayName = 'AssignCaseToOtherModal';

export default AssignCaseToOtherModal;

const UserToTeam = ({
  userLogged,
  users,
  searchUser,
  selectedUser,
  selectedTeam,
  setSearchUser,
  setSelectedUser,
  setSelectedTeam,
}: {
  userLogged: IUser;
  users: IUserInUserToAssignCase[];
  searchUser: string;
  selectedUser: IUserInUserToAssignCase | undefined;
  selectedTeam: ITeamWithUsersInUserToAssignCase | undefined;
  setSearchUser: (value: string) => void;
  setSelectedUser: (value: IUserInUserToAssignCase | undefined) => void;
  setSelectedTeam: (value: ITeamWithUsersInUserToAssignCase | undefined) => void;
}) => {
  const [localSearchTeam, setLocalSearchTeam] = useState('');

  const filteredTeams = useMemo(() => {
    const noTeamObj = {
      teamId: null,
      name: 'No team',
    };

    return [
      noTeamObj,
      ...(selectedUser?.teams?.filter((team) => team?.name?.toLowerCase().includes(localSearchTeam?.toLowerCase())) ||
        []),
    ];
  }, [selectedUser, localSearchTeam]);

  return (
    <div className="flex w-full h-[260px] gap-[8px]">
      <div className="flex-1 h-full flex flex-col">
        <div>
          <label htmlFor="" className="text-[#9D9D9C] text-[12px] font-normal leading-[12px]">
            Select User
          </label>
          <div className="relative">
            <Search className="absolute top-[50%] left-[8px] translate-y-[-50%] text-[#D9D9D9] w-[16px] h-[16px]" />
            <Input
              placeholder="Search team"
              className="w-full pl-[30px]"
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-col gap-[8px] flex-1 overflow-y-auto py-[8px]">
          {users?.map((user, index) => {
            const isSelected = selectedUser?.userId === user.userId;
            const isLoggedUser = user.userId === userLogged?.userId;

            return (
              <div
                key={index + 1}
                className={cn(
                  'flex flex-row items-center gap-[8px] hover:bg-[#0C8DBF] px-[16px] py-[8px] rounded-[8px] cursor-pointer group',
                  {
                    'bg-[#0C8DBF]': isSelected,
                  }
                )}
                onClick={(e) => {
                  handleDomBubbleEvent(e);
                  if (selectedTeam) {
                    setSelectedTeam(undefined);
                  }
                  setSelectedUser(user);
                }}
              >
                <Avatar
                  imgSrc={user.profileImage}
                  className="w-[24px] h-[24px] rounded-full bg-[#D9D9D9]"
                  username={user.fullname}
                  classNamePlaceholder="text-[12px] font-normal "
                />
                <span
                  className={cn('text-[12px] font-normal text-[#25151E] group-hover:text-white', {
                    'text-white': isSelected,
                  })}
                >
                  {user.fullname} {isLoggedUser ? '(You)' : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="h-full w-px bg-[#D9D9D9]"></div>
      <div className="flex-1 h-full flex flex-col">
        {selectedUser ? (
          <>
            <div>
              <label htmlFor="" className="text-[#9D9D9C] text-[12px] font-normal leading-[12px]">
                Select Team
              </label>
              <div className="relative">
                <Search className="absolute top-[50%] left-[8px] translate-y-[-50%] text-[#D9D9D9] w-[16px] h-[16px]" />
                <Input
                  placeholder="Search team"
                  className="w-full pl-[30px]"
                  value={localSearchTeam}
                  onChange={(e) => setLocalSearchTeam(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-[8px] flex-1 overflow-y-auto py-[8px]">
              {filteredTeams?.map((team, index) => {
                const isSelected = selectedTeam?.teamId === team.teamId;

                return (
                  <label
                    key={index + 1}
                    className={cn(
                      'flex flex-row items-center gap-[8px]  px-[16px] text-[#25151E] py-[8px] rounded-[8px] cursor-pointer group'
                    )}
                  >
                    <input
                      type="radio"
                      name="selectedTeam"
                      value={team.teamId || ''}
                      checked={isSelected}
                      onChange={() => {
                        setSelectedTeam(team as ITeamWithUsersInUserToAssignCase);
                      }}
                      className="sr-only"
                    />
                    <div
                      className={cn('w-[16px] h-[16px] rounded-full border flex items-center justify-center p-[2px]', {
                        'border-[#0C8DBF] ': isSelected,
                        'border-[#D9D9D9]': !isSelected,
                      })}
                    >
                      {isSelected && <div className="w-full h-full rounded-full bg-[#0C8DBF]"></div>}
                    </div>
                    <span className={cn('text-[12px] font-normal text-[#25151E] ')}>{team.name}</span>
                  </label>
                );
              })}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-[8px] flex-1 overflow-y-auto py-[8px] ">
            <span className="text-[12px] font-normal text-[#25151E] group-hover:text-white">
              Select user first to choose team
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const TeamToUser = ({
  userLogged,
  teams,
  searchTeam,
  selectedTeam,
  selectedUser,
  setSearchTeam,
  setSelectedTeam,
  setSelectedUser,
}: {
  userLogged: IUser;
  teams: ITeamWithUsersInUserToAssignCase[];
  searchTeam: string;
  selectedTeam: ITeamWithUsersInUserToAssignCase | undefined;
  selectedUser: IUserInUserToAssignCase | undefined;
  setSearchTeam: (value: string) => void;
  setSelectedTeam: (value: ITeamWithUsersInUserToAssignCase | undefined) => void;
  setSelectedUser: (value: IUserInUserToAssignCase | undefined) => void;
}) => {
  const [localSearchUser, setLocalSearchUser] = useState('');
  const filteredUsers = useMemo(() => {
    const result = [];
    const others = [];
    for (const userP of selectedTeam?.users || []) {
      if (userP.userId === userLogged?.userId) {
        result.push(userP);
      } else {
        others.push(userP);
      }
    }
    const reorderedUsers = result.concat(others);

    return reorderedUsers.filter((user) => user.fullname.toLowerCase().includes(localSearchUser.toLowerCase()));
  }, [selectedTeam, localSearchUser, userLogged]);

  return (
    <div className="flex w-full h-[260px] gap-[8px]">
      <div className="flex-1 h-full flex flex-col">
        <div>
          <label htmlFor="" className="text-[#9D9D9C] text-[12px] font-normal leading-[12px]">
            Select Team
          </label>
          <div className="relative">
            <Search className="absolute top-[50%] left-[8px] translate-y-[-50%] text-[#D9D9D9] w-[16px] h-[16px]" />
            <Input
              placeholder="Search team"
              className="w-full pl-[30px]"
              value={searchTeam}
              onChange={(e) => setSearchTeam(e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-col gap-[8px] flex-1 overflow-y-auto py-[8px]">
          {teams?.map((team, index) => {
            const isSelected = selectedTeam?.teamId === team.teamId;

            return (
              <label
                key={index + 1}
                className={cn(
                  'flex flex-row items-center gap-[8px]  px-[16px] text-[#25151E] py-[8px] rounded-[8px] cursor-pointer group'
                )}
              >
                <input
                  type="radio"
                  name="selectedTeam"
                  value={team.teamId || ''}
                  checked={isSelected}
                  onChange={() => {
                    if (selectedUser) {
                      setSelectedUser(undefined);
                    }
                    setSelectedTeam(team);
                  }}
                  className="sr-only"
                />
                <div
                  className={cn('w-[16px] h-[16px] rounded-full border flex items-center justify-center p-[2px]', {
                    'border-[#0C8DBF] ': isSelected,
                    'border-[#D9D9D9]': !isSelected,
                  })}
                >
                  {isSelected && <div className="w-full h-full rounded-full bg-[#0C8DBF]"></div>}
                </div>
                <span className={cn('text-[12px] font-normal text-[#25151E] ')}>{team.name}</span>
              </label>
            );
          })}
        </div>
      </div>
      <div className="h-full w-px bg-[#D9D9D9]"></div>
      <div className="flex-1 h-full flex flex-col">
        {selectedTeam ? (
          <>
            <div>
              <label htmlFor="" className="text-[#9D9D9C] text-[12px] font-normal leading-[12px]">
                Select User
              </label>
              <div className="relative">
                <Search className="absolute top-[50%] left-[8px] translate-y-[-50%] text-[#D9D9D9] w-[16px] h-[16px]" />
                <Input
                  placeholder="Search user"
                  className="w-full pl-[30px]"
                  value={localSearchUser}
                  onChange={(e) => setLocalSearchUser(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-[8px] flex-1 overflow-y-auto py-[8px]">
              {filteredUsers?.map((user, index) => {
                const isSelected = selectedUser?.userId === user.userId;
                const isLoggedUser = user.userId === userLogged?.userId;

                return (
                  <div
                    key={index + 1}
                    className={cn(
                      'flex flex-row items-center gap-[8px] hover:bg-[#0C8DBF] px-[16px] py-[8px] rounded-[8px] cursor-pointer group',
                      {
                        'bg-[#0C8DBF]': isSelected,
                      }
                    )}
                    onClick={(e) => {
                      handleDomBubbleEvent(e);
                      setSelectedUser(user as IUserInUserToAssignCase);
                    }}
                  >
                    <Avatar
                      imgSrc={user.profileImage}
                      className="w-[24px] h-[24px] rounded-full bg-[#D9D9D9]"
                      username={user.fullname}
                      classNamePlaceholder="text-[12px] font-normal "
                    />
                    <span
                      className={cn('text-[12px] font-normal text-[#25151E] group-hover:text-white', {
                        'text-white': isSelected,
                      })}
                    >
                      {user.fullname} {isLoggedUser ? '(You)' : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-[8px] flex-1 overflow-y-auto py-[8px] ">
            <span className="text-[12px] font-normal text-[#25151E] group-hover:text-white">
              Select team first to choose user
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const UserIcon = ({ className, ...props }: { className?: string } & SVGProps<SVGSVGElement>) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 16 21"
      fill="none"
      className={className}
      {...props}
    >
      <path
        d="M0 21C0 16.5817 3.58172 13 8 13C12.4183 13 16 16.5817 16 21H14C14 17.6863 11.3137 15 8 15C4.68629 15 2 17.6863 2 21H0ZM8 12C4.685 12 2 9.315 2 6C2 2.685 4.685 0 8 0C11.315 0 14 2.685 14 6C14 9.315 11.315 12 8 12ZM8 10C10.21 10 12 8.21 12 6C12 3.79 10.21 2 8 2C5.79 2 4 3.79 4 6C4 8.21 5.79 10 8 10Z"
        fill="currentColor"
      />
    </svg>
  );
};

const TeamIcon = ({ className, ...props }: { className?: string } & SVGProps<SVGSVGElement>) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      {...props}
    >
      <path
        d="M10 9C12.7614 9 15 11.2386 15 14V20H13V14C13 12.4023 11.7511 11.0963 10.1763 11.0051L10 11C8.4023 11 7.09634 12.2489 7.00509 13.8237L7 14V20H5V14C5 11.2386 7.23858 9 10 9ZM3.5 12C3.77885 12 4.05009 12.0326 4.3101 12.0942C4.14202 12.594 4.03873 13.122 4.00896 13.6693L4 14L4.0007 14.0856C3.88757 14.0456 3.76821 14.0187 3.64446 14.0069L3.5 14C2.7203 14 2.07955 14.5949 2.00687 15.3555L2 15.5V20H0V15.5C0 13.567 1.567 12 3.5 12ZM16.5 12C18.433 12 20 13.567 20 15.5V20H18V15.5C18 14.7203 17.4051 14.0796 16.6445 14.0069L16.5 14C16.3248 14 16.1566 14.03 16.0003 14.0852L16 14C16 13.3343 15.8916 12.694 15.6915 12.0956C15.9499 12.0326 16.2211 12 16.5 12ZM3.5 6C4.88071 6 6 7.11929 6 8.5C6 9.8807 4.88071 11 3.5 11C2.11929 11 1 9.8807 1 8.5C1 7.11929 2.11929 6 3.5 6ZM16.5 6C17.8807 6 19 7.11929 19 8.5C19 9.8807 17.8807 11 16.5 11C15.1193 11 14 9.8807 14 8.5C14 7.11929 15.1193 6 16.5 6ZM3.5 8C3.22386 8 3 8.2239 3 8.5C3 8.7761 3.22386 9 3.5 9C3.77614 9 4 8.7761 4 8.5C4 8.2239 3.77614 8 3.5 8ZM16.5 8C16.2239 8 16 8.2239 16 8.5C16 8.7761 16.2239 9 16.5 9C16.7761 9 17 8.7761 17 8.5C17 8.2239 16.7761 8 16.5 8ZM10 0C12.2091 0 14 1.79086 14 4C14 6.20914 12.2091 8 10 8C7.79086 8 6 6.20914 6 4C6 1.79086 7.79086 0 10 0ZM10 2C8.8954 2 8 2.89543 8 4C8 5.10457 8.8954 6 10 6C11.1046 6 12 5.10457 12 4C12 2.89543 11.1046 2 10 2Z"
        fill="currentColor"
      />
    </svg>
  );
};
